import React from "react";
import { formatNumber } from "../../../utils/formatters";
import { formatOpenRateSummary } from "./formatters";

interface EmailTrackingSummaryProps {
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  openRate: number | null;
  clickToOpenRate: number | null;
}

interface CardProps {
  label: string;
  value: string;
  testId: string;
  ariaLabel: string;
  tooltip?: string;
  accent?: boolean;
}

const Card: React.FC<CardProps> = ({ label, value, testId, ariaLabel, tooltip, accent = false }) => (
  <div
    className={`rounded-lg border p-4 shadow-sm ${
      accent ? "bg-primary-50 border-primary-200" : "bg-white border-neutral-200"
    }`}
    data-testid={testId}
    aria-label={ariaLabel}
  >
    <div className="flex items-center gap-1 mb-1">
      <div className="text-xs font-medium text-neutral-500 uppercase">
        {label}
      </div>
      {tooltip && (
        <div className="relative group">
          <button
            type="button"
            className="text-neutral-400 hover:text-neutral-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-full"
            aria-label={tooltip}
            data-testid={`${testId}-tooltip`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-white bg-neutral-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity z-20 pointer-events-none">
            {tooltip}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-900" />
          </div>
        </div>
      )}
    </div>
    <div
      className={`text-2xl ${
        accent ? "font-bold text-primary-800" : "font-semibold text-neutral-900"
      }`}
      data-testid={`${testId}-value`}
    >
      {value}
    </div>
  </div>
);

export const EmailTrackingSummaryCards: React.FC<EmailTrackingSummaryProps> = ({
  totalSent,
  totalOpens,
  totalClicks,
  openRate,
  clickToOpenRate,
}) => {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
      role="region"
      aria-label="Podsumowanie statystyk maili"
    >
      <Card
        label="Wysłane"
        value={formatNumber(totalSent)}
        testId="metric-total-sent"
        ariaLabel={`Wysłane maile: ${formatNumber(totalSent)}`}
        tooltip="Liczba wysłanych maili z alertami w wybranym okresie"
      />
      <Card
        label="Otwarcia"
        value={formatNumber(totalOpens)}
        testId="metric-total-opens"
        ariaLabel={`Otwarcia maili: ${formatNumber(totalOpens)}`}
        tooltip="Mail liczy się jako otwarty, gdy załadowano pixel lub kliknięto link. Pre-fetch Gmail/Apple może zawyżać liczbę, blokowanie pikseli może zaniżać."
      />
      <Card
        label="Współczynnik otwarć"
        value={formatOpenRateSummary(openRate)}
        testId="metric-open-rate"
        ariaLabel={`Współczynnik otwarć (open rate): ${formatOpenRateSummary(openRate)}`}
        tooltip="Otwarcia / wysłane. Bezpośrednia miara zaangażowania. Uwaga: Apple MPP (pre-fetch maili na iPhone) zawyża wynik. Blokery pikseli i klienty bez obrazków zaniżają — dlatego dla wiarygodnych metryk wykorzystujemy też kliknięcia (każdy klik traktujemy jako otwarcie)."
        accent
      />
      <Card
        label="Kliknięcia"
        value={formatNumber(totalClicks)}
        testId="metric-total-clicks"
        ariaLabel={`Kliknięcia: ${formatNumber(totalClicks)}`}
      />
      <Card
        label="Współczynnik klikalności"
        value={formatOpenRateSummary(clickToOpenRate)}
        testId="metric-click-to-open-rate"
        ariaLabel={`Współczynnik klikalności (click-to-open rate): ${formatOpenRateSummary(clickToOpenRate)}`}
        tooltip="Kliknięcia / otwarcia (CTOR). Mówi jak skuteczny jest content po otwarciu. Może przekroczyć 100% — to normalne: jedna osoba po otwarciu maila klika zwykle kilka różnych linków, a otwarcia liczone są jako unikalne maile."
        accent
      />
    </div>
  );
};

export default EmailTrackingSummaryCards;
